import ContactsAPI from "@/api/contacts";
import {
  Button,
  Option,
  SelectBox,
  SelectBoxProps,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import CreateContactFlyout from "../Flyouts/CreateContactFlyout";
import { Contact } from "../contacts.types";

type ContactRecord = {
  client_id: number;
  uuid: string;
  name: string;
  email: string;
};

export type ContactSelectorProps = Omit<
  SelectBoxProps,
  | "data"
  | "renderOption"
  | "OptionTooltip"
  | "searchFn"
  | "onScroll"
  | "searchable"
  | "loading"
  | "defaultValue"
  | "value"
> & {
  defaultValue?: Option;
  value?: Option;
  onContactCreated?: (newContact: Contact) => void;
  allowCreateNew?: boolean;
  query?: { case_id?: number; case_uuid?: string };
};

// Render like RoleSelector via renderOption prop
const ContactOptionComponent = ({ option }: { option: Partial<Option> }) => {
  const theme = useTheme();
  if (typeof option === "string") return <div>{option}</div>;
  const record = (option.data || {}) as ContactRecord;
  return (
    <div style={{ padding: "5px 0" }}>
      <div
        style={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "200px",
        }}
      >
        {record.name || option.label}
      </div>
      <div
        style={{
          fontSize: "12px",
          color: theme.palette.text.secondary,
          maxWidth: "200px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {record.email || ""}
      </div>
    </div>
  );
};

export const ContactSelector: React.FC<ContactSelectorProps> = (props) => {
  const { query: defaultQuery } = props;
  const [search, setSearch] = useState("");
  const [showCreateContactFlyout, setShowCreateContactFlyout] = useState(false);

  const query = {
    ...defaultQuery,
    search,
    pageSize: 100,
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["contacts", "list", query],
    queryFn: ({ pageParam = 1 }) => {
      return ContactsAPI.get({
        ...query,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    getPreviousPageParam: (firstPage: any) => {
      if (firstPage.page - 1 === 0) return null;
      return firstPage.page - 1;
    },
    initialPageParam: 1,
  });

  const records: ContactRecord[] =
    data?.pages?.reduce((acc, page) => {
      return [...acc, ...page.data];
    }, []) || [];

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (isFetchingNextPage) return;
    if (!hasNextPage) return;
    fetchNextPage();
  }, 50);

  const debouncedSearch = useDebouncedCallback(
    (val: string) => setSearch(val || ""),
    300
  );

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <=
      clientHeight + 100 * ((data?.pages?.length as number) || 0)
    ) {
      debouncedFetchNextPage();
    }
  };

  const onContactCreated = (newContact: Contact) => {
    refetch();
    props?.onContactCreated?.(newContact);
  };

  // Create action component for "Create New Contact"
  const actionComponent = props?.allowCreateNew ? (
    <Button
      size="xs"
      variant="subtle"
      color="primary"
      fullWidth
      style={{ marginBottom: 5 }}
      onClick={() => setShowCreateContactFlyout(true)}
    >
      + Create New Contact
    </Button>
  ) : undefined;

  // Combine number and name in label for client-side search by either
  const options: Option[] = records.map((item) => ({
    label: item.name,
    value: item.client_id,
    data: item,
  }));

  return (
    <>
      <SelectBox
        placeholder="Select or search for a contact..."
        label="Linked Contact"
        {...props}
        data={options}
        onScroll={handleScroll}
        searchable={true}
        searchFn={debouncedSearch}
        loading={isLoading}
        renderOption={(item) => {
          if (typeof item === "string") {
            return <div>{item}</div>;
          }
          return <ContactOptionComponent option={item} />;
        }}
        actionComponent={actionComponent}
      />
      <CreateContactFlyout
        open={showCreateContactFlyout}
        onSubmit={onContactCreated}
        handleClose={() => setShowCreateContactFlyout(false)}
        caseInfo={query.case_id ? { case_id: query.case_id } : null}
      />
    </>
  );
};
